export const FLOW_LAYOUTS = [
  { id: "radial", label: "Around a center" },
  { id: "topDown", label: "Top to bottom" },
  { id: "leftRight", label: "Left to right" },
];

export const FLOW_ARROW_HEADS = [
  { id: "arrow", label: "Arrow" },
  { id: "triangle", label: "Triangle" },
  { id: "diamond", label: "Diamond" },
  { id: "filled", label: "Filled diamond" },
  { id: "plus", label: "Circle plus" },
];

export const FLOW_ARROW_ENDS = [
  { id: "none", label: "None" },
  { id: "start", label: "Start" },
  { id: "end", label: "End" },
  { id: "both", label: "Both" },
];

export const FLOW_SHAPES = [
  { id: "rounded", label: "Rounded" },
  { id: "rectangle", label: "Rectangle" },
  { id: "circle", label: "Circle" },
  { id: "triangle", label: "Triangle" },
  { id: "rhombus", label: "Rhombus" },
  { id: "rhomboid", label: "Rhomboid" },
];

export const FILL_COLORS = [
  "#ffffff",
  "#f8fafc",
  "#dbeafe",
  "#dcfce7",
  "#fef9c3",
  "#fee2e2",
  "#f3e8ff",
  "#cffafe",
];

export const BORDER_COLORS = [
  "#334155",
  "#2563eb",
  "#15803d",
  "#b45309",
  "#b91c1c",
  "#7c3aed",
  "#0e7490",
  "#0f172a",
];

export const FLOW_SCHEMES = [
  { id: "paper", label: "Paper", fill: "#ffffff", border: "#334155" },
  { id: "slate", label: "Slate", fill: "#f8fafc", border: "#0f172a" },
  { id: "blue", label: "Blue", fill: "#dbeafe", border: "#2563eb" },
  { id: "green", label: "Green", fill: "#dcfce7", border: "#15803d" },
  { id: "amber", label: "Amber", fill: "#fef9c3", border: "#b45309" },
  { id: "red", label: "Red", fill: "#fee2e2", border: "#b91c1c" },
  { id: "violet", label: "Violet", fill: "#f3e8ff", border: "#7c3aed" },
  { id: "cyan", label: "Cyan", fill: "#cffafe", border: "#0e7490" },
];

export const FLOW_THEMES = [
  {
    id: "plain",
    label: "Plain",
    levels: [
      { fill: "#ffffff", border: "#334155" },
    ],
  },
  {
    id: "ocean",
    label: "Ocean",
    levels: [
      { fill: "#1e40af", border: "#172554" },
      { fill: "#dbeafe", border: "#2563eb" },
      { fill: "#ccfbf1", border: "#0f766e" },
      { fill: "#e0e7ff", border: "#4338ca" },
      { fill: "#cffafe", border: "#0891b2" },
    ],
  },
  {
    id: "forest",
    label: "Forest",
    levels: [
      { fill: "#166534", border: "#14532d" },
      { fill: "#dcfce7", border: "#15803d" },
      { fill: "#ecfccb", border: "#4d7c0f" },
      { fill: "#d1fae5", border: "#047857" },
      { fill: "#fef9c3", border: "#a16207" },
    ],
  },
  {
    id: "sunset",
    label: "Sunset",
    levels: [
      { fill: "#c2410c", border: "#9a3412" },
      { fill: "#ffedd5", border: "#ea580c" },
      { fill: "#fee2e2", border: "#dc2626" },
      { fill: "#fef3c7", border: "#d97706" },
      { fill: "#ffe4e6", border: "#e11d48" },
    ],
  },
  {
    id: "berry",
    label: "Berry",
    levels: [
      { fill: "#6d28d9", border: "#4c1d95" },
      { fill: "#f3e8ff", border: "#7c3aed" },
      { fill: "#fce7f3", border: "#db2777" },
      { fill: "#ede9fe", border: "#5b21b6" },
      { fill: "#fae8ff", border: "#a21caf" },
    ],
  },
  {
    id: "slate",
    label: "Slate",
    levels: [
      { fill: "#0f172a", border: "#020617" },
      { fill: "#e2e8f0", border: "#334155" },
      { fill: "#dbeafe", border: "#1d4ed8" },
      { fill: "#f1f5f9", border: "#475569" },
      { fill: "#e0e7ff", border: "#4338ca" },
    ],
  },
  {
    id: "clinical",
    label: "Clinical",
    levels: [
      { fill: "#0f766e", border: "#134e4a" },
      { fill: "#ccfbf1", border: "#0f766e" },
      { fill: "#dbeafe", border: "#1d4ed8" },
      { fill: "#dcfce7", border: "#15803d" },
      { fill: "#fef9c3", border: "#a16207" },
    ],
  },
];

export function nodeLevel(nodes, id) {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  let depth = 0;
  let current = byId.get(id);
  const seen = new Set();
  while (current?.parentId && byId.has(current.parentId) && !seen.has(current.id)) {
    seen.add(current.id);
    depth += 1;
    current = byId.get(current.parentId);
    if (depth > 40) break;
  }
  return depth;
}

export function themeLevelStyle(theme, level) {
  const levels = theme?.levels || [];
  if (!levels.length) return null;
  if (level <= 0 || levels.length === 1) return levels[0];
  return levels[((level - 1) % (levels.length - 1)) + 1];
}

export function noteColorsForTheme(theme, index = 0) {
  const levels = theme?.levels || [];
  if (!levels.length) return { headerFill: "#6b7280", bodyFill: "#ffffff" };
  const slot = levels[((index % levels.length) + levels.length) % levels.length];
  const next = levels[(((index + 1) % levels.length) + levels.length) % levels.length];
  const headerFill = textColorForFill(slot.fill) === "#ffffff" ? slot.fill : slot.border;
  const bodyFill = next !== slot && textColorForFill(next.fill) === "#1f2937" ? next.fill : "#ffffff";
  return { headerFill, bodyFill };
}

export function newFlowId(prefix) {
  return `${prefix}${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;
}

export function createFlowNode(parentId = null) {
  return {
    id: newFlowId("n"),
    parentId,
    text: "",
    shape: "rounded",
    fill: "#ffffff",
    borderColor: "#334155",
    borderWidth: 1.5,
  };
}

export function emptyFlowDocument() {
  return { nodes: [createFlowNode(null)], edges: {}, links: [], labels: [], notes: [], theme: null };
}

export function edgeKey(parentId, childId) {
  return `${parentId}:${childId}`;
}

export function textColorForFill(fill) {
  const hex = String(fill || "#ffffff").replace("#", "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex.padEnd(6, "0").slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (![r, g, b].every((n) => Number.isFinite(n))) return "#1f2937";
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#1f2937" : "#ffffff";
}

export function cloneDocument(document) {
  let source = document;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      source = null;
    }
  }
  source = source || emptyFlowDocument();
  return {
    nodes: (source.nodes || []).map((node) => ({ ...node })),
    edges: { ...(source.edges || {}) },
    links: (source.links || []).map((link) => ({ ...link })),
    labels: (source.labels || []).map((label) => ({ ...label })),
    notes: (source.notes || []).map((note) => ({
      ...note,
      sections: (note.sections || []).map((section) => ({ ...section })),
    })),
    theme: FLOW_THEMES.some((theme) => theme.id === source.theme) ? source.theme : null,
  };
}
